/*
 * Copyright (c) 2023. American Express Company
 */

// theirs
import React from 'react';
import { Alert, FieldControl, Label, Select, SelectOption } from '@americanexpress/dls-react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';

// ours
import { DropdownViewModel } from 'src/types/view-models/dropdown.view.model';
import Selectors from 'src/utils/selectors';

export default function Dropdown({
    id,
    label,
    children,
    defaultSelectOption,
    onChange,
    className,
    twoLined,
    hideLabel = false,
    allowEmptyOption = true,
    'data-cy': dataCy
}: DropdownViewModel): JSX.Element {
    const [field, meta, helpers]: [FieldInputProps<string>, FieldMetaProps<string>, FieldHelperProps<string>] =
        useField(id);
    const { setValue }: { setValue: (value: string) => void } = helpers;
    const hasError: boolean = meta.touched && meta.error?.length > 0;
    // DLS requires we associate Alert with Select in this particular way
    const alertId: string = `${id}Alert`;
    const describedby: { 'aria-describedby'?: string } = hasError ? { 'aria-describedby': alertId } : {};

    return (
        <FieldControl status={hasError ? 'error' : 'default'} data-cy={dataCy || `${id}Dropdown`} className={className}>
            {!hideLabel && label && <Label htmlFor={id}>{label}</Label>}
            <Select
                theme={hideLabel ? { marginTop: 0 } : {}}
                id={id}
                fluid
                value={field.value}
                {...describedby}
                {...field}
                onChange={(e: any) => {
                    setValue(e.target.value);
                    void onChange?.(e.target.value);
                }}
                twoLined={twoLined}
                aria-label={hideLabel ? label : null}
                data-cy={`${id}${Selectors.SpendSmart.SELECT}`}
            >
                {defaultSelectOption || (allowEmptyOption && <SelectOption value="" />)}
                {children}
            </Select>
            {hasError && (
                <Alert id={alertId} data-cy={`${id}${Selectors.SpendSmart.DROPDOWN_ERROR}`}>
                    {meta.error}
                </Alert>
            )}
        </FieldControl>
    );
}
